import { useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import ProductRow from './ProductRow';

const DEBOUNCE_MS = 350;
const EXAMPLES = ['דבש', 'חלב תנובה', 'במבה', '7290006901309'];

const s = {
  searchBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  input:     { flex: '1 1 280px', padding: '12px 14px', fontSize: 17, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', outline: 'none' },
  select:    { padding: '10px 12px', border: '1px solid #d0d7de', borderRadius: 10, background: '#fff' },
  error:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 16 },
  errorX:    { background: 'none', border: 'none', color: 'inherit', fontSize: 18, lineHeight: 1 },
  summary:   { display: 'flex', flexWrap: 'wrap', gap: '4px 16px', color: '#59636e', marginBottom: 12, fontSize: 14 },
  list:      { display: 'flex', flexDirection: 'column', gap: 10 },
  empty:     { textAlign: 'center', color: '#59636e', padding: '48px 16px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  chips:     { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 },
  chip:      { padding: '6px 12px', borderRadius: 999, border: '1px solid #d0d7de', background: '#fff', color: '#1f2328' },
};

const SearchTab = () => {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('relevance');
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState(null);          // null = עוד לא חיפשו
  const [pricesByItem, setPricesByItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);                         // מונע מתשובה ישנה לדרוס תשובה חדשה

  const query = q.trim();

  const load = async (text, sortBy, id) => {
    try {
      const resp = await API.searchProducts(text, sortBy);
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        // [1] stores, [2] items, [3] prices (ר' spSearchProducts ב-server/src/SQL/Products.sql)
        const byItem = {};
        (resp[3] || []).forEach((p) => {
          if (!byItem[p.itemId]) byItem[p.itemId] = {};
          byItem[p.itemId][p.storeId] = p;
        });
        setStores(resp[1] || []);
        setItems(resp[2] || []);
        setPricesByItem(byItem);
        setError(null);
      } else {
        setError(status?.msg || 'שגיאה בחיפוש');
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message || 'שגיאה בחיפוש');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    const id = ++requestId.current;
    if (query.length < 2) {
      setItems(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => load(query, sort, id), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, sort]);

  // באיזה סופר זול יותר, מתוך המוצרים המוצגים שקיימים ביותר מסופר אחד
  const cheaperCounts = useMemo(() => {
    const counts = {};
    (items || []).forEach((item) => {
      if (item.cheapestStoreId) counts[item.cheapestStoreId] = (counts[item.cheapestStoreId] || 0) + 1;
    });
    return stores.map((st) => ({ ...st, count: counts[st.id] || 0 })).filter((st) => st.count > 0);
  }, [items, stores]);

  const totalCount = items?.[0]?.totalCount ?? 0;

  return (
    <div>
      <div style={s.searchBar}>
        <input
          style={s.input}
          type="search"
          autoFocus
          placeholder="חיפוש לפי שם מוצר, ברקוד או חברה"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select style={s.select} value={sort} onChange={(e) => setSort(e.target.value)} aria-label="מיון">
          <option value="relevance">מיון: רלוונטיות</option>
          <option value="diff">מיון: הפרש מחיר גדול</option>
        </select>
      </div>

      {error && (
        <div style={s.error}>
          <span>{error}</span>
          <button style={s.errorX} onClick={() => setError(null)} aria-label="סגירה">×</button>
        </div>
      )}

      {query.length < 2 && (
        <div style={s.empty}>
          <div>הקלידו שם מוצר, ברקוד או שם חברה כדי לראות את המחיר בכל סופר</div>
          <div style={s.chips}>
            {EXAMPLES.map((ex) => (
              <button key={ex} style={s.chip} onClick={() => setQ(ex)}>{ex}</button>
            ))}
          </div>
        </div>
      )}

      {query.length >= 2 && items === null && loading && <div style={s.empty}>מחפש…</div>}

      {query.length >= 2 && items !== null && (
        <>
          <div style={s.summary}>
            <span>
              {loading ? 'מחפש…' : `נמצאו ${totalCount} מוצרים`}
              {totalCount > items.length ? ` (מוצגים ${items.length} הראשונים)` : ''}
            </span>
            {cheaperCounts.map((st) => (
              <span key={st.id}>זול יותר ב{st.name}: {st.count}</span>
            ))}
          </div>

          {items.length === 0 ? (
            <div style={s.empty}>לא נמצאו מוצרים עבור "{query}"</div>
          ) : (
            <div style={s.list}>
              {items.map((item) => (
                <ProductRow key={item.id} item={item} stores={stores} prices={pricesByItem[item.id] || {}} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchTab;
